// „Конфигурации" на склада: регистрация по ДДС (`vatRegistered`), складът по
// подразбиране за целия модул (`defaultWarehouseId`) и „Склад по продукт"
// (`ProductWarehousesSection` — своя заявка и свой запис). Всяка промяна се записва
// веднага и праща ВИНАГИ и двете полета (`settingsPayload`) — BE заменя целия
// ред, частичен PUT би занулил другото. Полетата са активни чак щом
// настройките се заредят: иначе запис в тази секунда би пратил
// стойностите по подразбиране вместо записаните.
//
// Без часовник и без ProfileDropdown — профилът е в лентата на sidebar-а.
import type { ComponentType, ReactNode } from "react";
import { App, Select, Spin } from "antd";
import { CloudServerOutlined, EuroOutlined, HomeOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  useInventorySettings,
  useUpdateInventorySettings,
  useWarehouses,
} from "../../../hooks/useInventory";
import { InventoryTheme } from "../components/InventoryTheme";
import { PageHeader } from "../components/list/PageHeader";
import { EmptyScreen, SectionCard, SegmentedCounts } from "../components/list/ListBits";
import {
  effectiveWarehouseId,
  settingsPayload,
  type SettingsValues,
} from "./settingsPayload";
import { ProductWarehousesSection } from "./ProductWarehousesSection";

// От общите props на складовите страници (Inventory.tsx) трябва само
// тесният изглед; бутонът за менюто е в PageHeader (useInventoryShell).
type Props = { isNarrow?: boolean };

// Една настройка: икон-чип, заглавие, обяснение и въпрос с контролата
const SettingsSection = ({
  Icon,
  title,
  hint,
  question,
  children,
}: {
  Icon: ComponentType<{ className?: string }>;
  title: string;
  hint: string;
  question: string;
  children: ReactNode;
}) => (
  <SectionCard className="flex flex-col gap-4 px-[22px] py-5">
    <div className="flex items-center gap-3">
      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-brand-tint text-brand-deep-ink">
        <Icon className="text-lg" />
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[15px] font-semibold leading-[1.2] text-ink">{title}</span>
        <span className="text-[12.5px] leading-[1.4] text-muted">{hint}</span>
      </div>
    </div>
    <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2">
      <span className="text-[13px] font-semibold text-body">{question}</span>
      {children}
    </div>
  </SectionCard>
);

export const SettingsPage = ({ isNarrow = false }: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const { data: settings, isLoading, isError } = useInventorySettings();
  const { data: warehouses = [] } = useWarehouses();
  const updateMutation = useUpdateInventorySettings();
  const saving = updateMutation.isPending;

  // Каквото се ПОКАЗВА — действащите стойности (при липса на запис BE връща
  // „регистриран по ДДС"; складът — записаният, иначе първият).
  const current: SettingsValues = {
    vatRegistered: settings?.vatRegistered ?? true,
    defaultWarehouseId: effectiveWarehouseId(settings?.defaultWarehouseId ?? null, warehouses),
  };

  const save = (patch: Partial<SettingsValues>) => {
    if (!settings || saving) return;
    updateMutation.mutate(settingsPayload(current, patch), {
      onSuccess: () => message.success(t("inventoryPage.settingsSaved")),
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { detail?: string } } };
        message.error(err.response?.data?.detail || t("inventoryPage.errSave"));
      },
    });
  };

  return (
    <InventoryTheme>
      <div className="flex h-full flex-col bg-canvas text-ink">
        <PageHeader title={t("inventoryPage.settings")} />
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Spin size="large" />
          </div>
        ) : isError || !settings ? (
          <EmptyScreen Icon={CloudServerOutlined} tone="brand" title={t("inventoryPage.errLoad")} />
        ) : (
          <div
            className={`min-h-0 flex-1 overflow-y-auto ${
              isNarrow ? "px-3 pb-10 pt-3.5" : "px-7 pb-12 pt-5"
            }`}
          >
            <div className="mx-auto flex max-w-[720px] flex-col gap-3.5">
              <SettingsSection
                Icon={EuroOutlined}
                title={t("inventoryPage.settingsVatSection")}
                hint={t("inventoryPage.settingsVatHint")}
                question={t("inventoryPage.settingsVatQuestion")}
              >
                {/* Докато записът върви, отметката не се пипа */}
                <div className={saving ? "pointer-events-none opacity-60" : ""}>
                  <SegmentedCounts<"yes" | "no">
                    value={current.vatRegistered ? "yes" : "no"}
                    onChange={(value) => {
                      const next = value === "yes";
                      if (next !== current.vatRegistered) save({ vatRegistered: next });
                    }}
                    options={[
                      { key: "yes", label: t("inventoryPage.settingsVatYes") },
                      { key: "no", label: t("inventoryPage.settingsVatNo") },
                    ]}
                  />
                </div>
              </SettingsSection>

              <SettingsSection
                Icon={HomeOutlined}
                title={t("inventoryPage.settingsWhSection")}
                hint={t("inventoryPage.settingsWhHint")}
                question={t("inventoryPage.settingsWhQuestion")}
              >
                <Select<number>
                  value={current.defaultWarehouseId ?? undefined}
                  onChange={(value) => save({ defaultWarehouseId: value ?? null })}
                  disabled={saving}
                  placeholder={t("inventoryPage.fieldWarehouse")}
                  options={warehouses.map((warehouse) => ({ value: warehouse.id, label: warehouse.name }))}
                  className={isNarrow ? "w-full" : "min-w-[220px]"}
                />
              </SettingsSection>

              <ProductWarehousesSection isNarrow={isNarrow} />
            </div>
          </div>
        )}
      </div>
    </InventoryTheme>
  );
};

export default SettingsPage;
